import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import HeroSection from './sections/HeroSection';
import FloatingWhatsApp from '../../components/common/FloatingWhatsApp';
import TrustBar from './sections/TrustBar';
import EligibilityChecker from './sections/EligibilityChecker';
import PricingSection from './sections/PricingSection';
import ProcessSection from './sections/ProcessSection';
import PenaltiesSection from './sections/PenaltiesSection';
import ComparisonSection from './sections/ComparisonSection';
import AboutSection from './sections/AboutSection';
import Testimonials from '../../components/common/Testimonials';
import FAQ from '../../components/common/FAQ';
import Modal from '../../components/common/Modal';
import { trackWhatsAppClick } from '../../utils/gtm';

// Load scoped corporate tax stylesheet
import './corporateTax.css';

const ctTestimonials = [
  {
    quote: "We have been working with Standard Auditing since 2016 for our auditing, accounts and taxation. They have proven both proficient and trustworthy.",
    name: "Ravichandran",
    meta: "Financial Manager, Silver Bright",
    avatar: "RA"
  },
  {
    quote: "Standard Auditing guided us through preparation, registration and compliance for VAT. Their advice was invaluable throughout.",
    name: "Alex Smith",
    meta: "Chief Executive, Financial Report Limited",
    avatar: "AS"
  },
  {
    quote: "An exemplary level of diligent and professional service. Their proactive approach is why we refer them across our network.",
    name: "Mohammed A Omar",
    meta: "Chief Executive, Fortune Consulting FZE",
    avatar: "MO"
  },
  {
    quote: "A longstanding partnership that has contributed a great deal to our journey. We are glad to give this reference.",
    name: "Senthil Kumar",
    meta: "Managing Director, Triway Technologies LLC",
    avatar: "SK"
  }
];

const ctFAQs = [
  {
    q: "Do I have to register for UAE Corporate Tax if my company made no profit?",
    a: "Yes. Registration is a separate obligation from paying tax. Every taxable person holding a UAE licence must register with the Federal Tax Authority and file a Corporate Tax return, even where the tax due is nil and even where the company was dormant for the whole period. The AED 10,000 administrative penalty is attached to registering late, not to owing tax."
  },
  {
    q: "What is the UAE Corporate Tax rate?",
    a: "UAE Corporate Tax is charged at 0 per cent on taxable income up to AED 375,000 and 9 per cent on taxable income above AED 375,000. Separately, a resident company whose revenue has never exceeded AED 3 million may elect Small Business Relief and be treated as having no taxable income for that period."
  },
  {
    q: "Who qualifies for Small Business Relief in the UAE?",
    a: "A resident person qualifies if revenue in the current tax period and in every previous tax period has stayed at or below AED 3 million. The test is cumulative, so exceeding AED 3 million once removes the relief for that period and for all future periods. Under Ministerial Decision No. 73 of 2023 the relief is available for tax periods from 1 June 2023 to 31 December 2026. It is not automatic and must be elected in the return on EmaraTax."
  },
  {
    q: "Are free zone companies exempt from UAE Corporate Tax?",
    a: "No. Free zone companies are not exempt, but a Qualifying Free Zone Person pays 0 per cent on qualifying income. That status depends on maintaining adequate substance in the zone, earning qualifying income, meeting transfer pricing requirements and preparing audited financial statements. Registration and filing remain compulsory, and failing a condition can move the company to the 9 per cent rate for that year and the following four."
  },
  {
    q: "When is my UAE Corporate Tax return due?",
    a: "A Corporate Tax return is due nine months after the end of the tax period. A company with a 31 December 2025 year end must file by 30 September 2026. A company with a 30 June year end files by 31 March. Corporate Tax has applied to financial years starting on or after 1 June 2023, so a company that has never filed may have more than one period outstanding."
  },
  {
    q: "What is the penalty for filing a UAE Corporate Tax return late?",
    a: "Under Cabinet Decision No. 75 of 2023, late filing attracts AED 500 for each month or part month during the first twelve months, then AED 1,000 for each month or part month from the thirteenth month. Late registration attracts a one off AED 10,000 penalty. Late payment attracts interest at 14 per cent a year on the unpaid tax under Cabinet Decision No. 129 of 2025. A delay of one day counts as a full month."
  },
  {
    q: "What documents are needed to file a UAE Corporate Tax return?",
    a: [
      "Trade licence, memorandum of association, Emirates ID and passport copy of the owner or authorised signatory, financial statements or a trial balance for the tax period, and your EmaraTax login if the company is already registered.",
      "If the bookkeeping is not in a filing-ready state we will say so on the call and quote that separately, rather than discovering it three days before the deadline."
    ]
  },
  {
    q: "Can I file my UAE Corporate Tax return myself?",
    a: [
      "Yes. Any company can file its own return on the EmaraTax portal, and for a dormant company with no transactions that is often a reasonable choice.",
      "The risk is not the submission, it is the positions taken in it: electing Small Business Relief without meeting the cumulative revenue test, claiming Qualifying Free Zone Person status without the required substance, or treating disallowable expenditure as deductible. Those errors surface later, when penalties and interest have already accrued."
    ]
  }
];

const CorporateTaxLanding = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isWaHovered, setIsWaHovered] = useState(false);

  const openModal = (e) => {
    if (e) e.preventDefault();
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  // Structured Data JSON-LD
  const jsonLdData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "AccountingService",
        "@id": "https://standardauditors.com/#organization",
        "name": "Standard Auditing",
        "alternateName": "Standard Auditing Services UAE",
        "description": "Audit, tax and accounting firm in Dubai providing UAE Corporate Tax registration and return filing, VAT, bookkeeping and statutory audit for mainland and free zone companies.",
        "url": "https://standardauditors.com/",
        "telephone": "+971-54-211-9784",
        "email": "Sales@standardauditors.com",
        "priceRange": "AED 750 to AED 1500 per Corporate Tax return",
        "currenciesAccepted": "AED",
        "foundingDate": "2000",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "NBQ Building 112, Khalid Bin Al Waleed Road, Al Hamriya",
          "addressLocality": "Dubai",
          "addressRegion": "Dubai",
          "addressCountry": "AE"
        },
        "areaServed": [
          { "@type": "Country", "name": "United Arab Emirates" },
          { "@type": "City", "name": "Dubai" },
          { "@type": "City", "name": "Sharjah" },
          { "@type": "City", "name": "Abu Dhabi" }
        ],
        "knowsAbout": [
          "UAE Corporate Tax",
          "Corporate Tax registration",
          "Corporate Tax return filing",
          "Small Business Relief",
          "Qualifying Free Zone Person",
          "EmaraTax portal",
          "Federal Tax Authority compliance",
          "VAT return filing UAE",
          "Statutory audit UAE",
          "IFRS reporting"
        ],
        "memberOf": {
          "@type": "Organization",
          "name": "Emirates Association for Accountants and Auditors"
        },
        "sameAs": [
          "https://www.instagram.com/standardauditing",
          "https://ae.linkedin.com/company/standard-auditingservicesuae",
          "https://x.com/StanAuditing",
          "https://youtube.com/@standardaudit"
        ]
      },
      {
        "@type": "WebPage",
        "@id": "https://audit-tax-consulting.standardauditors.com/#webpage",
        "url": "https://audit-tax-consulting.standardauditors.com/",
        "name": "UAE Corporate Tax Filing from AED 750 | Standard Auditing, Dubai",
        "description": "UAE Corporate Tax registration and return filing for mainland and free zone companies, from AED 750 plus VAT.",
        "inLanguage": "en-AE",
        "datePublished": "2026-07-31",
        "dateModified": "2026-07-31",
        "isPartOf": { "@id": "https://standardauditors.com/#organization" },
        "about": { "@id": "https://standardauditors.com/#organization" },
        "reviewedBy": { "@id": "https://standardauditors.com/#organization" }
      },
      {
        "@type": "Service",
        "@id": "https://audit-tax-consulting.standardauditors.com/#service",
        "serviceType": "UAE Corporate Tax registration and return filing",
        "name": "UAE Corporate Tax return filing",
        "description": "Preparation and submission of a UAE Corporate Tax return on the EmaraTax portal, including Corporate Tax registration support and an assessment of Small Business Relief eligibility.",
        "provider": { "@id": "https://standardauditors.com/#organization" },
        "areaServed": { "@type": "Country", "name": "United Arab Emirates" },
        "audience": {
          "@type": "BusinessAudience",
          "name": "UAE mainland and free zone companies with a trade licence"
        },
        "offers": [
          {
            "@type": "Offer",
            "name": "Filing only",
            "description": "For companies whose revenue has never exceeded AED 3 million and which qualify to elect Small Business Relief. Includes eligibility assessment, Corporate Tax registration support, return preparation, compliance check and submission on EmaraTax.",
            "price": "750",
            "priceCurrency": "AED",
            "eligibleCustomerType": "Business",
            "availability": "https://schema.org/InStock",
            "priceSpecification": {
              "@type": "PriceSpecification",
              "price": "750",
              "priceCurrency": "AED",
              "valueAddedTaxIncluded": false
            }
          },
          {
            "@type": "Offer",
            "name": "Filing and tax payable",
            "description": "For companies that must compute and pay Corporate Tax. Includes everything in Filing only plus full tax computation, review of financials and year end adjustments, compliance documentation and advice on allowable deductions.",
            "price": "1500",
            "priceCurrency": "AED",
            "eligibleCustomerType": "Business",
            "availability": "https://schema.org/InStock",
            "priceSpecification": {
              "@type": "PriceSpecification",
              "price": "1500",
              "priceCurrency": "AED",
              "valueAddedTaxIncluded": false
            }
          }
        ]
      },
      {
        "@type": "HowTo",
        "@id": "https://audit-tax-consulting.standardauditors.com/#howto",
        "name": "How to file a UAE Corporate Tax return with Standard Auditing",
        "description": "The four steps from first contact to a filed UAE Corporate Tax return.",
        "totalTime": "P14D",
        "estimatedCost": { "@type": "MonetaryAmount", "currency": "AED", "value": "750" },
        "step": [
          {
            "@type": "HowToStep",
            "position": 1,
            "name": "Free 20 minute review call",
            "text": "You share your trade licence, financial year end, latest financial statements and whether you already hold a Corporate Tax Registration Number."
          },
          {
            "@type": "HowToStep",
            "position": 2,
            "name": "Your position confirmed in writing",
            "text": "We confirm whether you must register, whether you can elect Small Business Relief, your exact filing deadline and the fixed fee, before any work starts."
          },
          {
            "@type": "HowToStep",
            "position": 3,
            "name": "You upload documents once",
            "text": "You send one fixed document list. We prepare the tax computation and the Corporate Tax return and come back to you only where a decision is genuinely needed."
          },
          {
            "@type": "HowToStep",
            "position": 4,
            "name": "Filed on EmaraTax with proof",
            "text": "We submit the return on the EmaraTax portal and send you the submission confirmation, the filed return and the working papers for your records."
          }
        ]
      },
      {
        "@type": "FAQPage",
        "@id": "https://audit-tax-consulting.standardauditors.com/#faq",
        "mainEntity": ctFAQs.map(f => ({
          "@type": "Question",
          "name": f.q,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": Array.isArray(f.a) ? f.a.join(' ') : f.a
          }
        }))
      }
    ]
  };

  return (
    <div className="ct-page-wrapper" style={{ background: '#ffffff', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Helmet>
        <title>UAE Corporate Tax Filing from AED 750 | Standard Auditing, Dubai</title>
        <meta name="description" content="FTA Corporate Tax registration and return filing for UAE mainland and free zone companies. Small Business Relief checked, return prepared, filed on EmaraTax. Fixed fee AED 750 or AED 1,500 plus VAT." />
        <link rel="canonical" href="https://audit-tax-consulting.standardauditors.com/corporate-tax" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Standard Auditing" />
        <meta property="og:locale" content="en_AE" />
        <meta property="og:url" content="https://audit-tax-consulting.standardauditors.com/corporate-tax" />
        <meta property="og:title" content="UAE Corporate Tax Filing from AED 750 | Standard Auditing, Dubai" />
        <meta property="og:description" content="FTA Corporate Tax registration and return filing for UAE mainland and free zone companies. Small Business Relief checked, return prepared, filed on EmaraTax. Fixed fee AED 750 or AED 1,500 plus VAT." />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="UAE Corporate Tax Filing from AED 750 | Standard Auditing, Dubai" />
        <meta name="twitter:description" content="Corporate Tax registration and return filing for UAE companies. Fixed fee from AED 750 plus VAT." />

        {/* JSON-LD Schema */}
        <script type="application/ld+json">
          {JSON.stringify(jsonLdData)}
        </script>
      </Helmet>

      <Header onOpenModal={openModal} />

      <main id="top" style={{ flex: 1 }}>
        <HeroSection />

        <TrustBar />

        <EligibilityChecker onOpenModal={openModal} />

        <PricingSection onOpenModal={openModal} />

        <ProcessSection />

        <PenaltiesSection />

        <ComparisonSection />

        <AboutSection />

        {/* Testimonials Slider */}
        <section className="sec sec--tint" id="testimonials">
          <div className="shell">
            <div className="sec-head sec-head--center rv">
              <h2 className="h2">What Our Clients Say</h2>
              <p className="answer">
                Real Google reviews from UAE businesses that trust Standard Auditing.
              </p>
            </div>
            <Testimonials testimonials={ctTestimonials} variant="ct" />
          </div>
        </section>

        {/* FAQ Accordions */}
        <section className="sec sec--tint" id="faq">
          <div className="shell">
            <div className="sec-head sec-head--center rv">
              <h2 className="h2">Frequently Asked Questions</h2>
              <p className="answer">
                UAE Corporate Tax questions we are asked most
              </p>
            </div>
            <FAQ faqs={ctFAQs} variant="ct" />
          </div>
        </section>

        {/* Final CTA */}
        <section className="sec final">
          <div className="shell">
            <div className="final-grid">
              <div className="rv">
                <h2 className="h2">Find out where you stand, before the deadline decides for you.</h2>
                <p>
                  A 20-minute call with a senior accountant. You will leave the call knowing your deadline,
                  your likely position, and what it will cost.
                </p>
                <div className="final-acts">
                  <button className="btn btn--onnavy btn--lg js-open-modal" onClick={openModal} aria-label="Book a free review, closing section">
                    Book a free review
                  </button>
                  <a
                    className="btn btn--lg"
                    href="https://wa.me/971542119784"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackWhatsAppClick('corporate_tax_cta')}
                    onMouseEnter={() => setIsWaHovered(true)}
                    onMouseLeave={() => setIsWaHovered(false)}
                    style={{
                      backgroundColor: isWaHovered ? 'var(--gold)' : 'transparent',
                      color: '#ffffff',
                      borderColor: isWaHovered ? 'var(--gold)' : 'rgba(255, 255, 255, .42)',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'all 0.25s ease',
                      transform: isWaHovered ? 'translateY(-2px)' : 'none',
                      boxShadow: isWaHovered ? '0 8px 20px rgba(163,121,34,.35)' : 'none',
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style={{ flexShrink: 0 }}>
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
                    </svg>
                    WhatsApp us
                  </a>
                </div>
              </div>

              <ul className="final-list rv">
                <li>
                  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <circle cx="12" cy="12" r="11" fill="rgba(255,255,255,.15)" />
                    <path d="M7.5 12.4l3 3 6-6.4" stroke="#e2b350" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span>No cost for the call and no obligation afterwards.</span>
                </li>
                <li>
                  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <circle cx="12" cy="12" r="11" fill="rgba(255,255,255,.15)" />
                    <path d="M7.5 12.4l3 3 6-6.4" stroke="#e2b350" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span>You speak to an accountant, not a call centre.</span>
                </li>
                <li>
                  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <circle cx="12" cy="12" r="11" fill="rgba(255,255,255,.15)" />
                    <path d="M7.5 12.4l3 3 6-6.4" stroke="#e2b350" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span>Your figures stay between you and us.</span>
                </li>
                <li>
                  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <circle cx="12" cy="12" r="11" fill="rgba(255,255,255,.15)" />
                    <path d="M7.5 12.4l3 3 6-6.4" stroke="#e2b350" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span>Office in Al Hamriya, Dubai. Come in if you prefer.</span>
                </li>
              </ul>
            </div>
          </div>
        </section>
      </main>

      <Footer />

       {/* Consult Modal Popup */}
      <Modal isOpen={isModalOpen} onClose={closeModal} />

      <FloatingWhatsApp />
    </div>
  );
};

export default CorporateTaxLanding;
